// Supabase client — single source for all frontend data/auth/storage calls.
// Import as: `import { supabase } from "@/integrations/supabase/client";`
//
// The URL + anon key are PUBLIC and injected at build/preview time by
// Coderblock as VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. NEVER put the
// service_role key in the frontend — privileged operations belong in Edge
// Functions.
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string) || "";
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || "";

/** True once the backend is provisioned and env is injected. Gate auth/data UI on this. */
export const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

if (!isSupabaseConfigured) {
  // Right after project creation the Supabase project is still provisioning, so
  // VITE_SUPABASE_URL/ANON_KEY may be empty for the first few seconds. Warn —
  // but DON'T pass empty strings to createClient(): supabase-js throws
  // "supabaseUrl is required" at import time, which white-screens the WHOLE app
  // (even pages that never touch Supabase). Reload once provisioning finishes.
  console.warn(
    "[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY not set yet — " +
      "backend still provisioning or env injection pending. Reload in a few seconds.",
  );
}

// Fall back to a syntactically-valid placeholder so createClient() NEVER throws
// at import. Network calls fail gracefully (handle `error`) until real env is
// present; the app still renders. Once env is injected, a reload wires it up.
export const supabase = createClient<Database>(
  SUPABASE_URL || "https://placeholder.supabase.co",
  SUPABASE_ANON_KEY || "placeholder-anon-key",
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  },
);

// "Invalid Refresh Token: Already Used" — the locally-stored session token was
// already consumed (preview restarts / stale tab). Clear the corrupted session
// once so autoRefreshToken stops retrying in a loop and the user can log in again.
const originalFetch = globalThis.fetch;
globalThis.fetch = async (...args) => {
  const response = await originalFetch(...args);
  try {
    const url = typeof args[0] === "string" ? args[0] : args[0] instanceof URL ? args[0].href : "";
    if (response.status === 400 && url.includes("/auth/v1/token?grant_type=refresh_token")) {
      await supabase.auth.signOut({ scope: "local" });
    }
  } catch {
    // never break the caller because of the interceptor
  }
  return response;
};
